import React, { useState, useEffect } from 'react';
import {
  collection, getDocs, doc, getDoc, writeBatch,
  addDoc, query, where, Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import * as XLSX from 'xlsx';

// ─────────────────────────────────────────────────────────────────────────────
// AdminHandoutTracker
//
// PURPOSE:
//   Admin tool to tick off T-shirts physically handed out to students.
//   Works regardless of whether the child has paid their admin fee.
//   Warns on unpaid students but still allows marking.
//   Saves correct fields to students collection and creates/updates
//   tshirtStock documents to keep everything in sync.
//
// HOW TO ADD TO AdminDashboard.js:
//   1. import AdminHandoutTracker from './AdminHandoutTracker';
//   2. if (currentView === 'handoutTracker') {
//        return <AdminHandoutTracker onClose={() => setCurrentView('dashboard')} />;
//      }
//   3. <button onClick={() => setCurrentView('handoutTracker')}>
//        👕 T-Shirt Handout Tracker
//      </button>
// ─────────────────────────────────────────────────────────────────────────────

const SIZES = [
  '2-3 YEARS','4-5 YEARS','5-6 YEARS','7-8 YEARS',
  '9-10 YEARS','11-12 YEARS','13-14 YEARS','S','M','L','XL','XXL','XXXL'
];

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  overlay: {
    position:'fixed', inset:0, background:'rgba(0,0,0,0.55)',
    display:'flex', alignItems:'flex-start', justifyContent:'center',
    zIndex:9999, overflowY:'auto', padding:'20px'
  },
  box: {
    background:'var(--color-background-primary, #fff)',
    borderRadius:'12px', width:'100%', maxWidth:'1050px',
    margin:'20px auto', boxShadow:'0 8px 40px rgba(0,0,0,0.25)'
  },
  header: {
    background:'linear-gradient(135deg,#1a1a6e 0%,#2B4BA0 100%)',
    padding:'18px 24px', borderRadius:'12px 12px 0 0',
    display:'flex', alignItems:'center', justifyContent:'space-between'
  },
  body: { padding:'24px' },
  closeBtn: {
    background:'rgba(255,255,255,0.18)', color:'#fff',
    border:'1px solid rgba(255,255,255,0.4)', borderRadius:'8px',
    padding:'8px 16px', cursor:'pointer', fontSize:'14px', fontWeight:600
  },
  btnPrimary: {
    background:'#667eea', color:'#fff', border:'none', borderRadius:'8px',
    padding:'11px 22px', fontSize:'14px', fontWeight:700, cursor:'pointer'
  },
  btnSuccess: {
    background:'#28a745', color:'#fff', border:'none', borderRadius:'8px',
    padding:'11px 22px', fontSize:'14px', fontWeight:700, cursor:'pointer'
  },
  btnGhost: {
    background:'transparent', color:'#667eea',
    border:'1.5px solid #667eea', borderRadius:'8px',
    padding:'8px 16px', fontSize:'13px', fontWeight:600, cursor:'pointer'
  },
  btnSecondary: {
    background:'#f0f0f0', color:'#333', border:'none', borderRadius:'8px',
    padding:'10px 18px', fontSize:'14px', fontWeight:600, cursor:'pointer'
  },
  select: {
    padding:'10px 12px', border:'2px solid #667eea', borderRadius:'8px',
    fontSize:'14px', background:'#fff', cursor:'pointer', width:'100%'
  },
  info: {
    background:'#e3f2fd', color:'#1565C0', padding:'12px 16px',
    borderRadius:'8px', marginBottom:'16px', border:'1px solid #90caf9',
    fontSize:'13px'
  },
  warn: {
    background:'#fff3cd', color:'#856404', padding:'10px 14px',
    borderRadius:'8px', border:'1px solid #ffc107', fontSize:'13px'
  },
  err: {
    background:'#f8d7da', color:'#721c24', padding:'12px 16px',
    borderRadius:'8px', marginBottom:'16px', border:'1px solid #f5c6cb',
    fontSize:'13px'
  },
  th: {
    padding:'9px 10px', textAlign:'left', fontSize:'11px', fontWeight:600,
    color:'#666', background:'#f8f9fa', borderBottom:'2px solid #dee2e6',
    position:'sticky', top:0
  },
  td: {
    padding:'8px 10px', borderBottom:'0.5px solid #eee',
    verticalAlign:'middle', fontSize:'13px'
  },
  badge: (bg, col) => ({
    display:'inline-block', padding:'2px 9px', borderRadius:'10px',
    fontSize:'11px', fontWeight:600, background:bg, color:col
  })
};

export default function AdminHandoutTracker({ onClose }) {

  // ── Steps ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState('pick');   // pick | track | done

  // ── Data ──────────────────────────────────────────────────────────────────
  const [franchisees, setFranchisees]     = useState([]);
  const [selFrId, setSelFrId]             = useState('');
  const [selFrName, setSelFrName]         = useState('');
  const [students, setStudents]           = useState([]);   // all active students
  const [existingStock, setExistingStock] = useState([]);   // tshirtStock docs

  // ── Per-student handout state ──────────────────────────────────────────────
  // { [studentId]: { ticked: bool, size: string, source: 'stock'|'order' } }
  const [handouts, setHandouts] = useState({});

  // ── UI ─────────────────────────────────────────────────────────────────────
  const [loadingFr,  setLoadingFr]  = useState(true);
  const [loadingSt,  setLoadingSt]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [filterMode, setFilterMode] = useState('all');
    // all | unticked | already | unpaid
  const [schoolFilter, setSchoolFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [saved,      setSaved]      = useState({ students:0, stock:[] });

  // ── Load franchisees ───────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'Franchisees'));
        const list = snap.docs
          .map(d => ({ id:d.id, name:d.data().name || d.id }))
          .sort((a,b) => a.name.localeCompare(b.name));
        setFranchisees(list);
      } catch(e) { setError('Could not load franchisees: ' + e.message); }
      finally    { setLoadingFr(false); }
    })();
  }, []);

  // ── Load students + stock for chosen franchisee ────────────────────────────
  const loadData = async () => {
    if (!selFrId) return;
    setLoadingSt(true); setError('');
    try {
      const frRef = doc(db, 'Franchisees', selFrId);

      // 1. All active students
      const snap = await getDocs(
        query(collection(db,'students'),
          where('franchiseeRef','==',frRef),
          where('isActive','==',true))
      );

      const list = snap.docs.map(d => {
        const r = d.data();
        const feePaid   = (r.termPayments?.admin || 0) >= (r.adminFee || 0);
        const hasShirt  = r.tshirtOrdered === true;
        const tshirtIncluded = r.adminFeeBreakdown?.tshirtIncluded !== false;
        return {
          id:            d.id,
          studentName:   r.studentName || '',
          schoolName:    r.schoolName  || '',
          tShirtSize:    r.tShirtSize  || '',
          tshirtOrdered: hasShirt,
          tshirtSource:  r.tshirtSource || '',
          tshirtOrderId: r.tshirtOrderId || null,
          stockAllocationId: r.stockAllocationId || null,
          adminFee:      r.adminFee || 0,
          adminFeePaid:  r.termPayments?.admin || 0,
          feePaid,
          tshirtIncluded,
          orderDate:     r.tshirtOrderDate?.toDate?.() || r.stockAllocationDate?.toDate?.() || null
        };
      });

      list.sort((a,b) => {
        if (a.schoolName !== b.schoolName) return a.schoolName.localeCompare(b.schoolName);
        return a.studentName.localeCompare(b.studentName);
      });

      // 2. Existing tshirtStock docs
      const stSnap = await getDocs(
        query(collection(db,'tshirtStock'), where('franchiseeRef','==',frRef))
      );
      const stock = stSnap.docs.map(d => ({ id:d.id, ...d.data() }));
      setExistingStock(stock);

      // 3. Pre-populate handouts for students already marked as ordered/allocated
      const initial = {};
      list.forEach(st => {
        initial[st.id] = {
          ticked: st.tshirtOrdered,   // already handed out = pre-ticked
          size:   st.tShirtSize || '',
          source: st.tshirtSource === 'stock' ? 'stock' : 'order'
        };
      });

      setStudents(list);
      setHandouts(initial);
      setStep('track');
    } catch(e) {
      setError('Error loading data: ' + e.message);
    } finally {
      setLoadingSt(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const setField = (id, field, val) =>
    setHandouts(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));

  const tickAll = (studentIds) =>
    setHandouts(prev => {
      const next = { ...prev };
      studentIds.forEach(id => { next[id] = { ...next[id], ticked:true }; });
      return next;
    });

  const schools = ['all', ...Array.from(new Set(students.map(s => s.schoolName))).sort()];

  const searchLower = searchText.toLowerCase().trim();

  const filtered = students.filter(st => {
    if (schoolFilter !== 'all' && st.schoolName !== schoolFilter) return false;
    const h = handouts[st.id] || {};
    if (filterMode === 'unticked'  && h.ticked)           return false;
    if (filterMode === 'already'   && !st.tshirtOrdered)  return false;
    if (filterMode === 'unpaid'    && st.feePaid)          return false;
    if (searchLower) {
      const nameMatch   = st.studentName.toLowerCase().includes(searchLower);
      const schoolMatch = st.schoolName.toLowerCase().includes(searchLower);
      if (!nameMatch && !schoolMatch) return false;
    }
    return true;
  });

  const tickedCount    = students.filter(s => handouts[s.id]?.ticked).length;
  const unpaidTicked   = students.filter(s => handouts[s.id]?.ticked && !s.feePaid).length;
  const changedCount   = students.filter(s => {
    const h = handouts[s.id] || {};
    const wasOrdered = s.tshirtOrdered;
    return h.ticked !== wasOrdered || (h.ticked && h.size !== s.tShirtSize);
  }).length;

  // Size breakdown of ticked students
  const sizeSummary = {};
  students.forEach(s => {
    const h = handouts[s.id];
    if (h?.ticked && h.size) {
      sizeSummary[h.size] = (sizeSummary[h.size] || 0) + 1;
    }
  });

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    // Validate — all ticked students need a size
    const noSize = students.filter(s => handouts[s.id]?.ticked && !handouts[s.id]?.size);
    if (noSize.length > 0) {
      setError(`Please select a size for: ${noSize.map(s => s.studentName).join(', ')}`);
      return;
    }

    const toChange = students.filter(s => {
      const h = handouts[s.id] || {};
      return h.ticked !== s.tshirtOrdered || (h.ticked && h.size !== s.tShirtSize);
    });

    if (toChange.length === 0) {
      setError('No changes to save.');
      return;
    }

    const unpaidInChanges = toChange.filter(s => !s.feePaid && handouts[s.id]?.ticked);
    const confirmMsg = [
      `Save handout records for ${toChange.length} student(s)?`,
      unpaidInChanges.length > 0
        ? `\n⚠️  ${unpaidInChanges.length} of these have NOT paid their admin fee:\n` +
          unpaidInChanges.map(s => `  • ${s.studentName} (${s.schoolName})`).join('\n')
        : '',
      '\nProceed?'
    ].join('');

    if (!window.confirm(confirmMsg)) return;

    setSaving(true); setError('');

    try {
      const batch  = writeBatch(db);
      const frRef  = doc(db, 'Franchisees', selFrId);
      const now    = new Date();

      // Track stock docs we need to create (by size)
      // Key = size, value = list of studentIds
      const stockToCreate = {};

      toChange.forEach(st => {
        const h       = handouts[st.id];
        const stRef   = doc(db, 'students', st.id);
        const isStock = h.source === 'stock';

        if (h.ticked) {
          // Mark as handed out
          if (isStock) {
            batch.update(stRef, {
              tshirtOrdered:      true,
              tshirtSource:       'stock',
              tShirtSize:         h.size,
              stockAllocationDate: Timestamp.fromDate(now),
              updatedAt:          Timestamp.fromDate(now)
            });
            if (!stockToCreate[h.size]) stockToCreate[h.size] = [];
            stockToCreate[h.size].push(st.id);
          } else {
            // 'order' source — update size, keep existing order linkage if present
            batch.update(stRef, {
              tshirtOrdered:    true,
              tshirtSource:     'order',
              tShirtSize:       h.size,
              tshirtOrderDate:  st.tshirtOrderId ? st.orderDate
                                  ? Timestamp.fromDate(st.orderDate)
                                  : Timestamp.fromDate(now)
                                : Timestamp.fromDate(now),
              updatedAt:        Timestamp.fromDate(now)
            });
          }
        } else {
          // Un-tick — reset student back to not ordered
          batch.update(stRef, {
            tshirtOrdered:      false,
            tshirtSource:       null,
            tShirtSize:         h.size || st.tShirtSize,
            tshirtOrderId:      null,
            tshirtOrderDate:    null,
            stockAllocationId:  null,
            stockAllocationDate:null,
            updatedAt:          Timestamp.fromDate(now)
          });
        }
      });

      await batch.commit();

      // Create tshirtStock docs for any stock handouts that don't have one
      // Group by size, create one doc per size if none already exists
      const createdStock = [];
      for (const [size, studentIds] of Object.entries(stockToCreate)) {
        // Check if a stock doc already exists for this franchisee + size
        const existing = existingStock.find(
          sk => sk.size === size && sk.franchiseeRef?.id === selFrId
        );
        if (!existing) {
          const stockRef = await addDoc(collection(db, 'tshirtStock'), {
            franchiseeRef:    frRef,
            franchiseeName:   selFrName,
            size,
            totalOrdered:     studentIds.length,
            allocated:        studentIds.length,
            remaining:        0,
            sourceOrderId:    'manual-handout',
            sourceOrderNumber:'MANUAL-HANDOUT',
            sourceOrderDate:  Timestamp.fromDate(now),
            allocations:      studentIds.map(sid => {
              const st = students.find(s => s.id === sid);
              return {
                studentRef:    doc(db, 'students', sid),
                studentName:   st?.studentName || sid,
                allocatedDate: Timestamp.fromDate(now),
                allocatedBy:   'admin-handout'
              };
            }),
            createdAt:  Timestamp.fromDate(now),
            updatedAt:  Timestamp.fromDate(now)
          });

          // Update students with the stock doc id
          const batch2 = writeBatch(db);
          studentIds.forEach(sid => {
            batch2.update(doc(db, 'students', sid), {
              stockAllocationId: stockRef.id
            });
          });
          await batch2.commit();

          createdStock.push({ size, count: studentIds.length, id: stockRef.id });
        }
      }

      setSaved({ students: toChange.length, stock: createdStock });
      setStep('done');

    } catch(e) {
      console.error('Save error:', e);
      setError('Error saving: ' + e.message);
    } finally {
      setSaving(false);
    }
  };


  // ── Download ───────────────────────────────────────────────────────────────
  const handleDownload = () => {
    const today = new Date().toLocaleDateString('en-ZA');
    const rows = students.map(st => {
      const h = handouts[st.id] || {};
      const ticked = h.ticked || false;
      return {
        'School':           st.schoolName,
        'Student Name':     st.studentName,
        'Size':             h.size || st.tShirtSize || '—',
        'Shirt Handed Out': ticked ? 'YES' : 'NO',
        'Source':           ticked ? (h.source === 'stock' ? 'From Stock' : 'New Order') : '—',
        'Admin Fee':        st.feePaid
                              ? 'Paid (R' + st.adminFeePaid + ')'
                              : 'Unpaid (R' + st.adminFeePaid + '/R' + st.adminFee + ')',
        'DB Status':        st.tshirtOrdered
                              ? (st.tshirtSource === 'stock' ? 'Stock' : 'Order')
                              : 'None',
      };
    });
    const summaryRows = [
      {},
      { 'School': '── SUMMARY ──' },
      { 'School': 'Total students',     'Student Name': students.length },
      { 'School': 'Shirts handed out',  'Student Name': students.filter(s => handouts[s.id] && handouts[s.id].ticked).length },
      { 'School': 'Not yet handed out', 'Student Name': students.filter(s => !handouts[s.id] || !handouts[s.id].ticked).length },
      { 'School': 'Unpaid but ticked',  'Student Name': unpaidTicked },
      {},
      { 'School': '── SIZES TICKED ──' },
      ...SIZES.filter(sz => sizeSummary[sz]).map(sz => ({ 'School': sz, 'Student Name': sizeSummary[sz] }))
    ];
    const ws = XLSX.utils.json_to_sheet([...rows, ...summaryRows]);
    ws['!cols'] = [{ wch:32 },{ wch:28 },{ wch:14 },{ wch:18 },{ wch:14 },{ wch:24 },{ wch:12 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Handout Report');
    const safeName = selFrName.replace(/\s+/g, '_');
    const safeDate = today.replace(/\//g, '-');
    XLSX.writeFile(wb, 'TShirt_Handout_' + safeName + '_' + safeDate + '.xlsx');
  };

  // ── RENDER ─────────────────────────────────────────────────────────────────
  return (
    <div style={s.overlay}>
      <div style={s.box}>

        {/* Header */}
        <div style={s.header}>
          <div>
            <h2 style={{ color:'#fff', margin:0, fontSize:'20px', fontWeight:700 }}>
              👕 T-Shirt Handout Tracker
            </h2>
            {step === 'track' && (
              <p style={{ color:'rgba(255,255,255,0.7)', margin:'4px 0 0', fontSize:'13px' }}>
                {selFrName} — tick every student who physically received a shirt
              </p>
            )}
          </div>
          <button style={s.closeBtn} onClick={onClose}>✕ Close</button>
        </div>

        <div style={s.body}>
          {error && <div style={s.err}>{error}</div>}

          {/* ── STEP 1: Pick franchisee ── */}
          {step === 'pick' && (
            <div style={{ maxWidth:480 }}>
              <div style={s.info}>
                Select a franchisee to load all their students. You can then
                tick off every child who physically received a shirt — whether
                or not they've paid their admin fee.
              </div>
              <label style={{ display:'block', fontWeight:600, marginBottom:6, color:'#333' }}>
                Franchisee
              </label>
              {loadingFr ? <p style={{ color:'#888' }}>Loading...</p> : (
                <select
                  style={s.select}
                  value={selFrId}
                  onChange={e => {
                    setSelFrId(e.target.value);
                    setSelFrName(franchisees.find(f => f.id === e.target.value)?.name || '');
                  }}
                >
                  <option value=''>-- Select franchisee --</option>
                  {franchisees.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              )}
              <button
                style={{ ...s.btnPrimary, marginTop:16, width:'100%',
                  opacity: !selFrId || loadingSt ? 0.6 : 1 }}
                disabled={!selFrId || loadingSt}
                onClick={loadData}
              >
                {loadingSt ? '⏳ Loading students...' : 'Load Students →'}
              </button>
            </div>
          )}

          {/* ── STEP 2: Handout table ── */}
          {step === 'track' && (
            <div>

              {/* Summary cards */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,minmax(0,1fr))', gap:10, marginBottom:20 }}>
                {[
                  { n: students.length,        l: 'Total students',     c: '#667eea' },
                  { n: tickedCount,             l: 'Ticked as received', c: '#28a745' },
                  { n: unpaidTicked,            l: 'Unpaid but ticked',  c: '#e65100' },
                  { n: changedCount,            l: 'Unsaved changes',    c: changedCount > 0 ? '#c62828' : '#888' }
                ].map(({ n, l, c }) => (
                  <div key={l} style={{ textAlign:'center', padding:'10px 6px',
                    background:'#f8f9fa', borderRadius:8 }}>
                    <div style={{ fontSize:24, fontWeight:700, color:c }}>{n}</div>
                    <div style={{ fontSize:11, color:'#666', marginTop:2 }}>{l}</div>
                  </div>
                ))}
              </div>

              {/* Size summary of ticked */}
              {tickedCount > 0 && (
                <div style={{ marginBottom:16, padding:'10px 14px', background:'#f0f4ff',
                  borderRadius:8, display:'flex', flexWrap:'wrap', gap:6, alignItems:'center' }}>
                  <span style={{ fontSize:12, fontWeight:600, color:'#555', marginRight:4 }}>
                    Sizes ticked:
                  </span>
                  {SIZES.filter(sz => sizeSummary[sz]).map(sz => (
                    <span key={sz} style={s.badge('#667eea','#fff')}>
                      {sz}: {sizeSummary[sz]}
                    </span>
                  ))}
                </div>
              )}

              {/* Search */}
              <div style={{ position:'relative', marginBottom:10 }}>
                <input
                  type='text'
                  placeholder='🔍  Search by student name or school...'
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  style={{
                    width:'100%', padding:'9px 36px 9px 12px',
                    border:'2px solid #667eea', borderRadius:8,
                    fontSize:14, boxSizing:'border-box', outline:'none'
                  }}
                />
                {searchText && (
                  <button
                    onClick={() => setSearchText('')}
                    style={{
                      position:'absolute', right:10, top:'50%',
                      transform:'translateY(-50%)',
                      background:'none', border:'none', cursor:'pointer',
                      fontSize:16, color:'#888', lineHeight:1
                    }}
                  >
                    2715</button>
                )}
              </div>

              {/* Filters */}
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:12, alignItems:'center' }}>
                {/* School filter */}
                <select
                  style={{ ...s.select, width:'auto', fontSize:'13px', padding:'7px 10px' }}
                  value={schoolFilter}
                  onChange={e => setSchoolFilter(e.target.value)}
                >
                  {schools.map(sc => (
                    <option key={sc} value={sc}>
                      {sc === 'all' ? 'All schools' : sc}
                    </option>
                  ))}
                </select>

                {/* Status filter */}
                {[
                  { key:'all',      label:`All (${students.length})` },
                  { key:'unticked', label:`Not yet ticked (${students.filter(s => !handouts[s.id]?.ticked).length})` },
                  { key:'already',  label:`Already ordered (${students.filter(s => s.tshirtOrdered).length})` },
                  { key:'unpaid',   label:`Unpaid (${students.filter(s => !s.feePaid).length})` },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setFilterMode(key)}
                    style={{
                      padding:'7px 12px', borderRadius:20, fontSize:12,
                      fontWeight: filterMode === key ? 700 : 500,
                      background: filterMode === key ? '#667eea' : '#f0f0f0',
                      color:      filterMode === key ? '#fff' : '#555',
                      border:     filterMode === key ? '2px solid #667eea' : '2px solid #ddd',
                      cursor:'pointer'
                    }}
                  >
                    {label}
                  </button>
                ))}

                {/* Result count when searching */}
                {searchText && (
                  <span style={{ fontSize:12, color:'#667eea', fontWeight:600 }}>
                    {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{searchText}"
                  </span>
                )}

                {/* Tick all visible */}
                <button
                  style={{ ...s.btnGhost, marginLeft:'auto', fontSize:12, padding:'7px 12px' }}
                  onClick={() => tickAll(filtered.map(s => s.id))}
                >
                  ✓ Tick all visible
                </button>
              </div>

              {unpaidTicked > 0 && (
                <div style={{ ...s.warn, marginBottom:12 }}>
                  ⚠️ <strong>{unpaidTicked} student{unpaidTicked !== 1 ? 's' : ''}</strong> ticked
                  as received but have NOT paid their admin fee. They are highlighted below.
                  You can still save — this is just a warning.
                </div>
              )}

              {/* Table */}
              <div style={{ overflowX:'auto', maxHeight:480, overflowY:'auto',
                border:'1px solid #dee2e6', borderRadius:8, marginBottom:16 }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13, tableLayout:'fixed' }}>
                  <thead>
                    <tr>
                      <th style={{ ...s.th, width:44 }}>Got it</th>
                      <th style={{ ...s.th, width:'22%' }}>Student</th>
                      <th style={{ ...s.th, width:'20%' }}>School</th>
                      <th style={{ ...s.th, width:'16%' }}>Size</th>
                      <th style={{ ...s.th, width:'13%' }}>Source</th>
                      <th style={{ ...s.th, width:'14%' }}>Fee</th>
                      <th style={{ ...s.th, width:'13%' }}>DB status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ ...s.td, textAlign:'center',
                          color:'#888', padding:24 }}>
                          No students in this filter
                        </td>
                      </tr>
                    )}
                    {filtered.map(st => {
                      const h          = handouts[st.id] || {};
                      const ticked     = h.ticked || false;
                      const isUnpaid   = !st.feePaid;
                      const isChanged  = ticked !== st.tshirtOrdered ||
                                         (ticked && h.size !== st.tShirtSize);
                      const rowBg      = ticked && isUnpaid  ? '#fff8e1'
                                       : ticked              ? '#f0fff4'
                                       : '#fff';

                      return (
                        <tr key={st.id} style={{ background: rowBg }}>
                          {/* Checkbox */}
                          <td style={{ ...s.td, textAlign:'center', padding:'6px 4px' }}>
                            <input
                              type='checkbox'
                              checked={ticked}
                              onChange={e => setField(st.id, 'ticked', e.target.checked)}
                              style={{ width:18, height:18, cursor:'pointer', accentColor:'#28a745' }}
                            />
                          </td>

                          {/* Name */}
                          <td style={s.td}>
                            <span style={{ fontWeight: isChanged ? 700 : 400 }}>
                              {st.studentName}
                            </span>
                            {isChanged && (
                              <span style={{ fontSize:10, color:'#c62828',
                                marginLeft:4, fontWeight:700 }}>CHANGED</span>
                            )}
                          </td>

                          {/* School */}
                          <td style={{ ...s.td, fontSize:12, color:'#555' }}>
                            {st.schoolName}
                          </td>

                          {/* Size select */}
                          <td style={s.td}>
                            <select
                              value={h.size || ''}
                              onChange={e => setField(st.id, 'size', e.target.value)}
                              disabled={!ticked}
                              style={{
                                padding:'4px 6px', borderRadius:6, fontSize:12,
                                border: !ticked ? '1px solid #eee'
                                  : h.size !== st.tShirtSize && st.tShirtSize
                                  ? '2px solid #e65100'
                                  : '2px solid #28a745',
                                background: !ticked ? '#f8f8f8' : '#fff',
                                width:'100%', cursor: ticked ? 'pointer' : 'default'
                              }}
                            >
                              <option value=''>-- size --</option>
                              {SIZES.map(sz => (
                                <option key={sz} value={sz}>{sz}</option>
                              ))}
                            </select>
                          </td>

                          {/* Source */}
                          <td style={s.td}>
                            {ticked ? (
                              <select
                                value={h.source || 'stock'}
                                onChange={e => setField(st.id, 'source', e.target.value)}
                                style={{
                                  padding:'4px 6px', borderRadius:6, fontSize:12,
                                  border:'1.5px solid #ccc', background:'#fff',
                                  width:'100%', cursor:'pointer'
                                }}
                              >
                                <option value='stock'>📦 Stock</option>
                                <option value='order'>🆕 Order</option>
                              </select>
                            ) : (
                              <span style={{ color:'#bbb', fontSize:12 }}>—</span>
                            )}
                          </td>

                          {/* Fee status */}
                          <td style={s.td}>
                            {isUnpaid ? (
                              <span style={s.badge('#fff3cd','#856404')}>
                                ⚠️ Unpaid
                              </span>
                            ) : (
                              <span style={s.badge('#d4edda','#155724')}>
                                ✅ Paid
                              </span>
                            )}
                          </td>

                          {/* DB status */}
                          <td style={s.td}>
                            {st.tshirtOrdered ? (
                              <span style={s.badge(
                                st.tshirtSource === 'stock' ? '#e3f2fd' : '#e8f5e9',
                                st.tshirtSource === 'stock' ? '#1565C0' : '#2e7d32'
                              )}>
                                {st.tshirtSource === 'stock' ? '📦 Stock' : '🆕 Order'}
                              </span>
                            ) : (
                              <span style={s.badge('#f5f5f5','#888')}>
                                ⭕ None
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Footer */}
              <div style={{ display:'flex', alignItems:'center',
                justifyContent:'space-between', flexWrap:'wrap', gap:12,
                paddingTop:14, borderTop:'1px solid #eee' }}>
                <div style={{ fontSize:13, color:'#555' }}>
                  <strong style={{ color: changedCount > 0 ? '#c62828' : '#28a745' }}>
                    {changedCount}
                  </strong> unsaved change{changedCount !== 1 ? 's' : ''}
                  {unpaidTicked > 0 && (
                    <span style={{ color:'#e65100', marginLeft:10 }}>
                      · {unpaidTicked} unpaid students ticked
                    </span>
                  )}
                </div>
                <div style={{ display:'flex', gap:10 }}>
                  <button
                    style={s.btnSecondary}
                    onClick={() => { setStep('pick'); setStudents([]); setHandouts({}); }}
                  >
                    ← Back
                  </button>
                  <button
                    style={{ ...s.btnSecondary, background:'#e8f5e9', color:'#2e7d32',
                      border:'1.5px solid #a5d6a7' }}
                    onClick={handleDownload}
                    disabled={students.length === 0}
                  >
                    📥 Download Report
                  </button>
                  <button
                    style={{ ...s.btnSuccess,
                      opacity: changedCount === 0 || saving ? 0.5 : 1 }}
                    disabled={changedCount === 0 || saving}
                    onClick={handleSave}
                  >
                    {saving
                      ? '⏳ Saving...'
                      : `💾 Save ${changedCount} Change${changedCount !== 1 ? 's' : ''}`}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 3: Done ── */}
          {step === 'done' && (
            <div style={{ textAlign:'center', padding:'32px 20px' }}>
              <div style={{ fontSize:56, marginBottom:12 }}>✅</div>
              <h2 style={{ color:'#28a745', marginBottom:8 }}>
                {saved.students} Student{saved.students !== 1 ? 's' : ''} Updated
              </h2>
              <p style={{ color:'#555', marginBottom:20 }}>
                Handout records saved for <strong>{selFrName}</strong>.
              </p>

              {saved.stock.length > 0 && (
                <div style={{ ...s.info, textAlign:'left', marginBottom:20 }}>
                  <strong>New stock records created:</strong>
                  <ul style={{ margin:'8px 0 0', paddingLeft:20 }}>
                    {saved.stock.map(sk => (
                      <li key={sk.id}>
                        {sk.size} — {sk.count} shirt{sk.count !== 1 ? 's' : ''} allocated
                        <span style={{ fontSize:11, color:'#888', marginLeft:8 }}>
                          (doc: {sk.id})
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div style={{ display:'flex', gap:12, justifyContent:'center' }}>
                <button
                  style={s.btnSecondary}
                  onClick={() => {
                    setStep('pick'); setStudents([]); setHandouts([]);
                    setSelFrId(''); setSelFrName(''); setSaved({ students:0, stock:[] });
                  }}
                >
                  Track Another Franchisee
                </button>
                <button style={s.btnPrimary} onClick={onClose}>
                  Done — Close
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}